import { useState } from 'react';
import { api } from '../../api/client.js';
import {
  STORE_HERO_AUTOPLAY_DEFAULT,
  STORE_HERO_AUTOPLAY_MAX,
  STORE_HERO_AUTOPLAY_MIN,
  STORE_HERO_MAX_SLIDES,
  STORE_HERO_SIZE_HINT,
  clampHeroAutoplaySeconds,
  normalizeHeroSlide,
} from '../../theme/storeHero.js';

/**
 * Editor do hero Magalu/Havan (até 6 slides só-imagem).
 *
 * @param {{
 *   tenantId: number|null,
 *   enabled: boolean,
 *   onEnabledChange: (v: boolean) => void,
 *   autoplaySeconds: number,
 *   onAutoplayChange: (n: number) => void,
 *   slides: unknown[],
 *   onSlidesChange: (slides: unknown[]) => void,
 *   sectionOptions?: string[],
 *   disabled?: boolean,
 * }} props
 */
export function StoreHeroForm({
  tenantId,
  enabled,
  onEnabledChange,
  autoplaySeconds,
  onAutoplayChange,
  slides,
  onSlidesChange,
  sectionOptions = [],
  disabled = false,
}) {
  const [uploadBusy, setUploadBusy] = useState(false);
  const [uploadErr, setUploadErr] = useState('');
  const list = Array.isArray(slides) ? slides : [];

  function commit(next) {
    onSlidesChange(next.slice(0, STORE_HERO_MAX_SLIDES));
  }

  function patchSlide(index, patch) {
    const next = list.map((s, i) => {
      if (i !== index) return s;
      const cur = s && typeof s === 'object' ? { ...s } : {};
      return { ...cur, ...patch };
    });
    commit(next);
  }

  function addSlide() {
    if (list.length >= STORE_HERO_MAX_SLIDES) return;
    commit([
      ...list,
      {
        enabled: true,
        image_url: '',
        link_type: 'none',
        secao: '',
        product_id: null,
        href: '',
      },
    ]);
  }

  function removeSlide(index) {
    commit(list.filter((_, i) => i !== index));
  }

  async function uploadImage(index, file) {
    if (!tenantId || !file) return;
    setUploadErr('');
    setUploadBusy(true);
    try {
      const fd = new FormData();
      fd.append('imagem', file);
      const r = await api(`/tenants/${tenantId}/vitrine-hero-upload`, { method: 'POST', body: fd });
      const url = r?.url;
      if (typeof url !== 'string' || !url.trim()) {
        setUploadErr('Resposta do servidor sem URL da imagem.');
        return;
      }
      patchSlide(index, { image_url: url.trim() });
    } catch (e) {
      setUploadErr(e?.message || 'Falha no envio da imagem.');
    } finally {
      setUploadBusy(false);
    }
  }

  return (
    <div className="space-y-4 rounded-xl border border-zinc-200 bg-zinc-50/80 p-4 dark:border-zinc-700 dark:bg-zinc-900/50">
      <label className="flex items-start gap-2 text-sm font-medium text-zinc-800 dark:text-zinc-100">
        <input
          type="checkbox"
          className="mt-0.5"
          checked={Boolean(enabled)}
          disabled={disabled}
          onChange={(e) => onEnabledChange(e.target.checked)}
        />
        <span>
          Activar hero rotativo (layout padrão)
          <span className="mt-0.5 block text-xs font-normal text-zinc-500">
            Estilo Magalu/Havan — só imagem. Funciona sem a vitrine modular; se a vitrine estiver
            ligada, usa o mesmo carrossel.
          </span>
        </span>
      </label>

      <div className="flex flex-wrap items-end gap-3">
        <label className="text-xs font-semibold uppercase tracking-wide text-zinc-500">
          Segundos entre slides
          <input
            type="number"
            min={STORE_HERO_AUTOPLAY_MIN}
            max={STORE_HERO_AUTOPLAY_MAX}
            step={1}
            disabled={disabled || !enabled}
            className="mt-1 block w-24 rounded-lg border border-zinc-300 bg-white px-2 py-1.5 text-sm dark:border-zinc-600 dark:bg-zinc-950"
            value={clampHeroAutoplaySeconds(autoplaySeconds || STORE_HERO_AUTOPLAY_DEFAULT)}
            onChange={(e) => onAutoplayChange(clampHeroAutoplaySeconds(e.target.value))}
          />
        </label>
        <p className="pb-1.5 text-xs text-zinc-500">
          Tamanho recomendado: <strong>{STORE_HERO_SIZE_HINT}</strong> · máx. {STORE_HERO_MAX_SLIDES}{' '}
          banners
        </p>
      </div>

      {uploadErr ? (
        <p className="text-sm text-red-600 dark:text-red-400">{uploadErr}</p>
      ) : null}

      <ul className="space-y-3">
        {list.map((raw, i) => {
          const s = normalizeHeroSlide(raw) || {
            enabled: true,
            image_url: '',
            link_type: 'none',
            secao: '',
            product_id: null,
            href: '',
          };
          return (
            <li
              key={i}
              className="rounded-lg border border-zinc-200 bg-white p-3 dark:border-zinc-700 dark:bg-zinc-950"
            >
              <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                <label className="flex items-center gap-2 text-sm font-medium">
                  <input
                    type="checkbox"
                    checked={s.enabled}
                    disabled={disabled}
                    onChange={(e) => patchSlide(i, { enabled: e.target.checked })}
                  />
                  Banner {i + 1}
                </label>
                <button
                  type="button"
                  className="text-xs font-semibold text-red-600 hover:underline disabled:opacity-40"
                  disabled={disabled}
                  onClick={() => removeSlide(i)}
                >
                  Remover
                </button>
              </div>
              <div className="grid gap-2 sm:grid-cols-2">
                <label className="text-xs font-medium text-zinc-600 dark:text-zinc-400">
                  Imagem
                  <input
                    type="file"
                    accept="image/*"
                    disabled={disabled || uploadBusy || !tenantId}
                    className="mt-1 block w-full text-xs"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) void uploadImage(i, f);
                      e.target.value = '';
                    }}
                  />
                  {s.image_url ? (
                    <span className="mt-1 block truncate text-[10px] text-zinc-400">{s.image_url}</span>
                  ) : null}
                </label>
                <label className="text-xs font-medium text-zinc-600 dark:text-zinc-400">
                  Ao clicar
                  <select
                    disabled={disabled}
                    className="mt-1 block w-full rounded-lg border border-zinc-300 bg-white px-2 py-1.5 text-sm dark:border-zinc-600 dark:bg-zinc-900"
                    value={s.link_type}
                    onChange={(e) => patchSlide(i, { link_type: e.target.value })}
                  >
                    <option value="none">Nada</option>
                    <option value="secao">Categoria da loja</option>
                    <option value="product">Produto (ID)</option>
                    <option value="url">Link externo</option>
                  </select>
                </label>
              </div>
              {s.link_type === 'secao' ? (
                <label className="mt-2 block text-xs font-medium text-zinc-600 dark:text-zinc-400">
                  Categoria (secção)
                  {sectionOptions.length > 0 ? (
                    <select
                      disabled={disabled}
                      className="mt-1 block w-full rounded-lg border border-zinc-300 bg-white px-2 py-1.5 text-sm dark:border-zinc-600 dark:bg-zinc-900"
                      value={s.secao}
                      onChange={(e) => patchSlide(i, { secao: e.target.value })}
                    >
                      <option value="">— escolha —</option>
                      {sectionOptions.map((sec) => (
                        <option key={sec} value={sec}>
                          {sec}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type="text"
                      disabled={disabled}
                      className="mt-1 block w-full rounded-lg border border-zinc-300 px-2 py-1.5 text-sm dark:border-zinc-600 dark:bg-zinc-900"
                      value={s.secao}
                      placeholder="Nome exacto da secção"
                      onChange={(e) => patchSlide(i, { secao: e.target.value })}
                    />
                  )}
                </label>
              ) : null}
              {s.link_type === 'product' ? (
                <label className="mt-2 block text-xs font-medium text-zinc-600 dark:text-zinc-400">
                  ID do produto
                  <input
                    type="number"
                    min={1}
                    disabled={disabled}
                    className="mt-1 block w-36 rounded-lg border border-zinc-300 px-2 py-1.5 text-sm dark:border-zinc-600 dark:bg-zinc-900"
                    value={s.product_id ?? ''}
                    onChange={(e) => {
                      const n = Number(e.target.value);
                      patchSlide(i, {
                        product_id: Number.isFinite(n) && n >= 1 ? Math.trunc(n) : null,
                      });
                    }}
                  />
                </label>
              ) : null}
              {s.link_type === 'url' ? (
                <label className="mt-2 block text-xs font-medium text-zinc-600 dark:text-zinc-400">
                  URL
                  <input
                    type="url"
                    disabled={disabled}
                    className="mt-1 block w-full rounded-lg border border-zinc-300 px-2 py-1.5 text-sm dark:border-zinc-600 dark:bg-zinc-900"
                    value={s.href}
                    placeholder="https://"
                    onChange={(e) => patchSlide(i, { href: e.target.value })}
                  />
                </label>
              ) : null}
            </li>
          );
        })}
      </ul>

      <button
        type="button"
        disabled={disabled || list.length >= STORE_HERO_MAX_SLIDES}
        className="rounded-lg border border-zinc-300 bg-white px-3 py-1.5 text-sm font-semibold text-zinc-800 disabled:opacity-40 dark:border-zinc-600 dark:bg-zinc-900 dark:text-zinc-100"
        onClick={addSlide}
      >
        + Adicionar banner ({list.length}/{STORE_HERO_MAX_SLIDES})
      </button>
    </div>
  );
}
